import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Button,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  Tab,
  Tabs,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Snackbar,
  Alert
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import ArticleIcon from '@mui/icons-material/Article';
import DescriptionIcon from '@mui/icons-material/Description';
import PsychologyIcon from '@mui/icons-material/Psychology';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import { getProject } from '../utils/api';
import { 
  processDocument, 
  processProject, 
  queryProject, 
  generateFromSOW, 
  syncProjectDocuments,
  extractSOWInsights,
  directDocumentQuery
} from '../utils/aiService';
import { 
  getSowInsights, 
  generateAndSaveSowInsights, 
  updateSowInsights,
  autoSaveSowInsights,
  clearAutoSave
} from '../utils/sowInsightsService';
import { useAuth } from '../contexts/AuthContext';

// Tab Panel component
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`insights-tabpanel-${index}`}
      aria-labelledby={`insights-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const ProjectInsightsPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [insights, setInsights] = useState({
    summary: null,
    requirements: [],
    keyFeatures: [],
    timelines: [],
    stakeholders: [],
    risks: []
  });
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  
  // State for document querying
  const [queryInput, setQueryInput] = useState('');
  const [queryResult, setQueryResult] = useState(null);
  const [queryLoading, setQueryLoading] = useState(false);
  
  // Fetch project details
  const fetchProject = async () => {
    try {
      setLoading(true);
      const response = await getProject(projectId);
      if (response.success) {
        setProject(response.project);
      } else {
        setStatusMessage('Failed to load project details');
        showSnackbar('Failed to load project details', 'error');
      }
    } catch (error) {
      console.error('Error fetching project:', error);
      setStatusMessage('Error loading project details');
      showSnackbar('Error loading project details', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch SOW insights from database
  const fetchSowInsights = async () => {
    try {
      const response = await getSowInsights(projectId);
      
      if (response.has_insights) {
        const insights = response.insights;
        setInsights({
          summary: insights.summary,
          requirements: insights.requirements || [],
          keyFeatures: insights.key_features || [],
          timelines: insights.timeline || [],
          stakeholders: insights.stakeholders || [],
          risks: insights.risks || []
        });
        
        setStatusMessage('Insights loaded from database');
      } else {
        // No insights in database, reset to empty state
        setInsights({
          summary: null,
          requirements: [],
          keyFeatures: [],
          timelines: [],
          stakeholders: [],
          risks: []
        });
      }
    } catch (error) {
      console.error('Error fetching SOW insights:', error);
      // Don't show error for missing insights, just keep empty state
    }
  };

  // Initial data loading
  useEffect(() => {
    fetchProject();
    fetchSowInsights();
  }, [projectId]);

  // Auto-save insights when they change
  useEffect(() => {
    if (project && insights && (
      insights.summary || 
      insights.requirements.length > 0 || 
      insights.keyFeatures.length > 0 || 
      insights.timelines.length > 0 || 
      insights.stakeholders.length > 0 || 
      insights.risks.length > 0
    )) {
      const insightsToSave = {
        summary: insights.summary,
        requirements: insights.requirements,
        key_features: insights.keyFeatures,
        timeline: insights.timelines,
        stakeholders: insights.stakeholders,
        risks: insights.risks
      };
      
      autoSaveSowInsights(projectId, insightsToSave);
    }
    
    return () => {
      clearAutoSave();
    };
  }, [insights, projectId, project]);

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      clearAutoSave();
    };
  }, []);
  
  // Handle tab change
  const handleChangeTab = (event, newValue) => {
    setTabValue(newValue);
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({
      ...prev,
      open: false
    }));
  };
  
  // Enhanced parser for extracting structured information from AI responses
  const parseBulletedList = (text) => {
    if (!text) return [];
      
    // Ensure text is a string
    if (typeof text !== 'string') {
      console.warn('parseBulletedList received non-string input:', text);
      // Convert to string if possible
      try {
        if (typeof text === 'object') {
          text = JSON.stringify(text);
        } else {
          text = String(text);
        }
      } catch (e) {
        console.error('Failed to convert input to string:', e);
        return [];
      }
    }
      
    // First try to parse the whole text as a structured response
    try {
      // Check if this might be a JSON response
      if (text.includes('{') && text.includes('}')) {
        const jsonMatch = text.match(/```json\s*([\s\S]*?)\s*```/) ||
                           text.match(/{[\s\S]*?}/);
        if (jsonMatch) {
          const jsonContent = JSON.parse(jsonMatch[0]);
          if (Array.isArray(jsonContent)) {
            return jsonContent;
          } else if (jsonContent.items && Array.isArray(jsonContent.items)) {
            return jsonContent.items;
          }
        }
      }
    } catch (e) {
      console.log("Not valid JSON, continuing with text parsing");
    }
      
    // Try to extract a list from markdown format
    const markdownListRegex = /^\s*([*\-+]|\d+\.)\s+(.+)$/gm;
    const markdownMatches = [...text.matchAll(markdownListRegex)];
    if (markdownMatches.length > 0) {
      return markdownMatches.map(match => match[2].trim());
    }
      
    // Split by common bullet formats
    const bulletRegexes = [
      /^[\s]*[•\-*]\s*(.*)/gm,           // Bullet points
      /^[\s]*(\d+)[\.\)]\s*(.*)/gm,      // Numbered lists
      /^[\s]*[A-Z\d]+[\.\)]\s*(.*)/gm    // Alphanumeric lists (A., 1., etc.)
    ];
      
    for (const regex of bulletRegexes) {
      const matches = [...text.matchAll(regex)];
      if (matches.length > 0) {
        return matches.map(match => match[1] || match[0]).filter(item => item.trim().length > 0);
      }
    }
      
    // If no bullet formats found, try splitting by lines and filtering
    const lines = text.split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 10 && !line.startsWith('#')); // Filter out short lines and headers
      
    if (lines.length > 1) {
      return lines;
    }
      
    // Last resort: If it's a paragraph, try to split by sentences for key points
    if (text.length > 100) {
      const sentences = text.match(/[^.!?]+[.!?]+/g) || [];
      const keyPoints = sentences.filter(s => s.trim().length > 15 && s.length < 200);
      if (keyPoints.length > 0) {
        return keyPoints.map(s => s.trim());
      }
    }
      
    // If we still have content but couldn't parse it into a list, return it as a single item
    return text.trim() ? [text.trim()] : [];
  };
  
  // Query document directly (bypassing vector database)
  const queryDocumentDirectly = async (documentId, question) => {
    if (!question.trim()) {
      showSnackbar('Please enter a question', 'warning');
      return;
    }
    
    try {
      setQueryLoading(true);
      setQueryResult(null);
      
      const result = await directDocumentQuery(documentId, question);
      
      if (result.success) {
        setQueryResult(result);
        setQueryInput('');
      } else {
        console.error("Failed to query document:", result.error);
        showSnackbar(`Failed to query document: ${result.error}`, 'error');
      }
    } catch (error) {
      console.error("Error querying document:", error);
      showSnackbar(`Error querying document: ${error.message}`, 'error');
    } finally {
      setQueryLoading(false);
    }
  };
  
  // Direct process of SOW document
  const processSOWDocument = async (sowDoc) => {
    try {
      const metadata = {
        document_type: 'SOW',
        process_with_enhanced_sow: true,
        project_id: projectId,
        force_reprocess: true
      };
      
      console.log(`Processing project documents ${sowDoc.document_id} with metadata:`, metadata);
      
      const result = await processDocument(sowDoc.document_id, metadata);
      
      if (!result.success) {
        console.error("Failed to process project documents:", result.error);
        showSnackbar(`Failed to process project documents: ${result.error}`, 'error');
        return false;
      }
      
      console.log("Project documents processed successfully:", result);
      showSnackbar("Project documents processed successfully", 'success');
      return true;
    } catch (error) {
      console.error("Error processing project documents:", error);
      showSnackbar(`Error processing project documents: ${error.message}`, 'error');
      return false;
    }
  };
  
  // Generate insights from project documents using AI and save to database
  const generateInsights = async () => {
    if (!project) return;
    
    // Check if project has SOW document
    const hasSOW = project.documents && 
                  project.documents.sow && 
                  project.documents.sow.length > 0;
    
    if (!hasSOW) {
      setStatusMessage('No project documents found. Please upload project documents first.');
      showSnackbar('No project documents found. Please upload project documents first.', 'warning');
      return;
    }
    
    try {
      setInsightsLoading(true);
      // Reset insights state to ensure UI resets
      setInsights({
        summary: null,
        requirements: [],
        keyFeatures: [],
        timelines: [],
        stakeholders: [],
        risks: []
      });
      
      setStatusMessage('Analyzing project documents and extracting insights...');
      
      // Use the new service that generates and automatically saves to database
      const result = await generateAndSaveSowInsights(projectId);
      
      if (!result.success) {
        setStatusMessage(`Failed to extract insights: ${result.error}`);
        showSnackbar(`Failed to extract insights: ${result.error}`, 'error');
        return;
      }
      
      // Check if we have insights in the response
      if (result.insights) {
        const { insights } = result;
        
        // Update all insights at once
        setInsights({
          summary: insights.summary || null,
          requirements: insights.requirements || [],
          keyFeatures: insights.key_features || [],
          timelines: insights.timeline || [],
          stakeholders: insights.stakeholders || [],
          risks: insights.risks || []
        });
        
        // Check if we received any insights
        const hasReceivedAnyInsights = 
          !!insights.summary || 
          insights.requirements?.length > 0 || 
          insights.key_features?.length > 0 || 
          insights.timeline?.length > 0 || 
          insights.stakeholders?.length > 0 || 
          insights.risks?.length > 0;
        
        if (hasReceivedAnyInsights) {
          setStatusMessage('Insights generated and saved successfully');
          showSnackbar('Insights generated and saved to database successfully', 'success');
        } else {
          setStatusMessage('No insights could be extracted from the project documents.');
          showSnackbar('No insights could be extracted. Please try uploading more comprehensive project documents.', 'warning');
        }
      } else {
        setStatusMessage('No insights were returned from the extraction process.');
        showSnackbar('No insights were returned. Please try again.', 'warning');
      }
    } catch (error) {
      console.error('Error generating insights:', error);
      setStatusMessage('Error generating insights: ' + error.message);
      showSnackbar('Error generating insights: ' + error.message, 'error');
    } finally {
      setInsightsLoading(false);
    }
  };
  
  // Loading state
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }
  
  // Project not found
  if (!project) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h5" color="error">
          Project not found
        </Typography>
        <Button 
          variant="text" 
          startIcon={<ArrowBackIcon />} 
          onClick={() => navigate('/projects')}
          sx={{ mt: 2 }}
        >
          Back to Projects
        </Button>
      </Container>
    );
  }
  
  // Get SOW document
  const sowDocument = project.documents && 
                     project.documents.sow && 
                     project.documents.sow.length > 0 ? 
                     project.documents.sow[0] : null;
  
  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton 
            onClick={() => navigate(`/projects/${projectId}`)} 
            aria-label="back"
            sx={{ mr: 1 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            {project.name} - Insights
          </Typography>
        </Box>
        
        <Button 
          variant="contained" 
          color="primary"
          startIcon={<AnalyticsIcon />}
          onClick={generateInsights}
          disabled={insightsLoading}
        >
          {insightsLoading ? 'Generating...' : 'Generate Insights'}
        </Button>
      </Box>
      
      {/* SOW Info */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Project Documents</Typography>
          
          {sowDocument && (
            <IconButton 
              onClick={() => {/* Download function would go here */}}
              aria-label="download"
            >
              <DownloadIcon />
            </IconButton>
          )}
        </Box>
        <Divider sx={{ my: 2 }} />
        
        {sowDocument ? (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                <ArticleIcon />
              </Avatar>
              <Box>
                <Typography variant="subtitle1">{sowDocument.title}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Uploaded on {new Date(sowDocument.created_at).toLocaleDateString()}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PsychologyIcon />}
                  onClick={() => processSOWDocument(sowDocument)}
                  sx={{ mt: 1, mr: 1 }}
                >
                  Process Project Documents
                </Button>
              </Box>
            </Box>
            
            {/* New Ask AI Box */}
            <Box sx={{ mt: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid #e0e0e0' }}>
              <Typography variant="subtitle1" gutterBottom>
                Ask AI about this document
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ flexGrow: 1, mr: 2 }}>
                  <input
                    value={queryInput}
                    onChange={(e) => setQueryInput(e.target.value)}
                    placeholder="Ask a question about this document..."
                    style={{ 
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      fontSize: '16px'
                    }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !queryLoading) {
                        queryDocumentDirectly(sowDocument.document_id, queryInput);
                      }
                    }}
                  />
                </Box>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => queryDocumentDirectly(sowDocument.document_id, queryInput)}
                  disabled={queryLoading || !queryInput.trim()}
                >
                  {queryLoading ? 'Asking...' : 'Ask AI'}
                </Button>
              </Box>
              
              {queryLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                  <CircularProgress size={30} />
                </Box>
              )}
              
              {queryResult && (
                <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    <strong>Q: {queryResult.query}</strong>
                  </Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {queryResult.answer}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        ) : (
          <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
            No project documents have been uploaded for this project.
            Please upload project documents to generate insights.
          </Typography>
        )}
      </Paper>
      
      {/* Status Message */}
      {statusMessage && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'info.light', color: 'info.contrastText' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {insightsLoading && <CircularProgress size={20} sx={{ mr: 2 }} />}
            <Typography variant="body1">
              {statusMessage}
            </Typography>
          </Box>
        </Paper>
      )}
      
      {/* Insights Tabs */}
      <Paper sx={{ width: '100%', mb: 4 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={tabValue} 
            onChange={handleChangeTab} 
            aria-label="insights tabs"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Summary" />
            <Tab label="Requirements" />
            <Tab label="Features" />
            <Tab label="Timeline" />
            <Tab label="Stakeholders" />
            <Tab label="Risks" />
          </Tabs>
        </Box>
        
        {/* Summary Tab */}
        <TabPanel value={tabValue} index={0}>
          <Typography variant="h6" gutterBottom>
            Project Summary
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          {insights.summary ? (
            <Box sx={{ whiteSpace: 'pre-wrap' }}>
              <Typography variant="body1">
                {insights.summary}
              </Typography>
            </Box>
          ) : (
            <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              {insightsLoading ? 
                'Generating project summary...' : 
                'Click the "Generate Insights" button to analyze the project documents.'}
            </Typography>
          )}
        </TabPanel>
        
        {/* Requirements Tab */}
        <TabPanel value={tabValue} index={1}>
          <Typography variant="h6" gutterBottom>
            Business Requirements
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          {insights.requirements && insights.requirements.length > 0 ? (
            <List>
              {insights.requirements.map((req, index) => (
                <ListItem key={index} alignItems="flex-start">
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      {index + 1}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText primary={req} />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              {insightsLoading ? 
                'Extracting business requirements...' : 
                'No business requirements have been identified yet.'}
            </Typography>
          )}
        </TabPanel>
        
        {/* Features Tab */}
        <TabPanel value={tabValue} index={2}>
          <Typography variant="h6" gutterBottom>
            Key Features and Deliverables
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          {insights.keyFeatures && insights.keyFeatures.length > 0 ? (
            <Grid container spacing={2}>
              {insights.keyFeatures.map((feature, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                        <AutoAwesomeIcon color="primary" sx={{ mr: 1, mt: 0.5 }} />
                        <Typography variant="body1">{feature}</Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              {insightsLoading ? 
                'Identifying key features...' : 
                'No key features have been identified yet.'}
            </Typography>
          )}
        </TabPanel>
        
        {/* Timeline Tab */}
        <TabPanel value={tabValue} index={3}>
          <Typography variant="h6" gutterBottom>
            Project Timeline
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          {insights.timelines && insights.timelines.length > 0 ? (
            <List>
              {insights.timelines.map((timeline, index) => (
                <ListItem key={index}>
                  <ListItemText 
                    primary={timeline} 
                    secondary={`Milestone ${index + 1}`}
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              {insightsLoading ? 
                'Extracting timeline information...' : 
                'No timeline information has been identified yet.'}
            </Typography>
          )}
        </TabPanel>
        
        {/* Stakeholders Tab */}
        <TabPanel value={tabValue} index={4}>
          <Typography variant="h6" gutterBottom>
            Project Stakeholders
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          {insights.stakeholders && insights.stakeholders.length > 0 ? (
            <List>
              {insights.stakeholders.map((stakeholder, index) => (
                <ListItem key={index}>
                  <ListItemAvatar>
                    <Avatar>
                      {stakeholder.charAt(0)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText primary={stakeholder} />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              {insightsLoading ? 
                'Identifying stakeholders...' : 
                'No stakeholders have been identified yet.'}
            </Typography>
          )}
        </TabPanel>
        
        {/* Risks Tab */}
        <TabPanel value={tabValue} index={5}>
          <Typography variant="h6" gutterBottom>
            Project Risks & Challenges
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          {insights.risks && insights.risks.length > 0 ? (
            <Grid container spacing={2}>
              {insights.risks.map((risk, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <Paper 
                    variant="outlined" 
                    sx={{ 
                      p: 2, 
                      bgcolor: 'warning.light',
                      color: 'warning.contrastText'
                    }}
                  >
                    <Typography variant="body1">{risk}</Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              {insightsLoading ? 
                'Identifying risks and challenges...' : 
                'No risks or challenges have been identified yet.'}
            </Typography>
          )}
        </TabPanel>
      </Paper>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ProjectInsightsPage;
